import React, { memo } from "react";
import { classes } from "@bloomberg/media-ui-core";
import styles from "./style.module.css";
import type { TableColumnDefinition } from "~types/table";

type TableCellProps<T> = {
  column: TableColumnDefinition<T>;
  className?: string;
  rowData: T;
};

function getCellValue<T, K extends keyof T>(
  rowData: T,
  key: K
): React.ReactNode {
  const value = rowData[key];

  if (
    typeof value === "string" ||
    typeof value === "number" ||
    React.isValidElement(value)
  ) {
    // Return strings, numbers, and React elements directly
    return value;
  } else if (Array.isArray(value)) {
    // Convert arrays to comma-separated strings
    return value.join(", ");
  } else if (typeof value === "object" && value !== null) {
    // Convert objects to JSON strings
    return JSON.stringify(value);
  } else if (value === undefined) {
    // Handle undefined values
    return "N/A";
  }

  // Return null for any other unhandled types
  return null;
}

function TableCell<T>(props: TableCellProps<T>) {
  const { column, className, rowData } = props;

  const cellContent = column.renderer
    ? column.renderer(rowData, column)
    : getCellValue(rowData, column.key as keyof T);

  return (
    <td
      className={classes([
        styles.tableCell,
        column.className,
        className,
        column.align === "start" && styles.start,
        column.align === "end" && styles.end,
      ])}
    >
      <span
        className={classes([
          column.align === "start" && styles.cellStart,
          column.align === "end" && styles.cellEnd,
        ])}
      >
        {cellContent}
      </span>
    </td>
  );
}

export default memo(TableCell) as typeof TableCell;
